/**
 * SCR-021 — Điểm danh lớp (Teacher)
 * Route: /teacher/classes/:classId/attendance
 * Mobile-first: date picker + list HV dọc 1 cột, sticky summary + action bar at bottom.
 * Desktop: same layout (table optional enhancement).
 * Session closed → form locked + banner. Default all present, toggle exceptions.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { AlertCircle, CheckSquare, RefreshCw, Save, Lock, LayoutDashboard } from 'lucide-react';
import { useEffect, useMemo, useReducer, useRef } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  useAttendanceByDate,
  useAttendanceSessions,
  useOpenAttendanceSession,
  useSaveAttendance,
  useCloseAttendanceSession,
} from '@client/lib/queries/use-teacher';

export const Route = createFileRoute('/teacher/classes/$classId/attendance')({
  component: AttendanceTab,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type AttStatus = 'present' | 'absent' | 'late' | 'excused';

interface LocalRecord {
  enrollmentId: string;
  studentCode: string;
  fullName: string;
  status: AttStatus;
  checkInAt: string;
  note: string;
}

type Action =
  | { type: 'init'; records: LocalRecord[] }
  | { type: 'set_all_present' }
  | { type: 'set_status'; enrollmentId: string; status: AttStatus }
  | { type: 'set_note'; enrollmentId: string; note: string }
  | { type: 'set_checkin'; enrollmentId: string; checkInAt: string };

function recordsReducer(state: LocalRecord[], action: Action): LocalRecord[] {
  switch (action.type) {
    case 'init':
      return action.records;
    case 'set_all_present':
      return state.map((r) => ({ ...r, status: 'present', checkInAt: '', note: '' }));
    case 'set_status':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, status: action.status } : r,
      );
    case 'set_note':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, note: action.note } : r,
      );
    case 'set_checkin':
      return state.map((r) =>
        r.enrollmentId === action.enrollmentId ? { ...r, checkInAt: action.checkInAt } : r,
      );
    default:
      return state;
  }
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
function AttendanceTab() {
  const { classId } = Route.useParams();
  const [selectedDate, setSelectedDate] = useDateState(todayStr());

  const { data: attData, isLoading, isError, refetch } = useAttendanceByDate(classId, selectedDate);
  const { data: sessionsData } = useAttendanceSessions(classId);

  const openSession = useOpenAttendanceSession(classId);
  const closeSessionMut = useCloseAttendanceSession(
    classId,
    attData?.session?.id ?? '',
  );

  const [records, dispatch] = useReducer(recordsReducer, []);
  const prevSessionIdRef = useRef<string | null>(null);

  // Sync server records → local state when session/date changes
  useEffect(() => {
    if (!attData) return;
    const sid = attData.session?.id ?? null;
    if (sid === prevSessionIdRef.current && records.length > 0) return; // already synced
    prevSessionIdRef.current = sid;

    const local: LocalRecord[] = attData.records.map((r) => ({
      enrollmentId: r.enrollmentId,
      studentCode: r.student.studentCode,
      fullName: r.student.fullName,
      status: r.status as AttStatus,
      checkInAt: r.checkInAt ?? '',
      note: r.note ?? '',
    }));
    dispatch({ type: 'init', records: local });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attData?.session?.id, selectedDate]);

  const session = attData?.session ?? null;
  const isClosed = session?.status === 'closed';
  const hasSession = !!session;

  // Summary counts
  const summary = useMemo(() => {
    const counts = { present: 0, absent: 0, late: 0, excused: 0 };
    records.forEach((r) => { counts[r.status] = (counts[r.status] ?? 0) + 1; });
    return counts;
  }, [records]);

  // ---------------------------------------------------------------------------
  // Save handler
  // ---------------------------------------------------------------------------
  const saveSession = useSaveAttendance(classId, session?.id ?? '');

  async function handleSave(close = false) {
    if (!session) return;
    try {
      await saveSession.mutateAsync({
        records: records.map((r) => ({
          enrollmentId: r.enrollmentId,
          status: r.status,
          checkInAt: r.checkInAt || undefined,
          note: r.note || undefined,
        })),
      });
      if (close) {
        await closeSessionMut.mutateAsync();
        toast.success('Đã chốt buổi điểm danh.');
      } else {
        toast.success('Đã lưu điểm danh.');
      }
    } catch {
      toast.error('Lỗi khi lưu điểm danh. Dữ liệu đã nhập vẫn được giữ.');
    }
  }

  async function handleOpenSession() {
    try {
      await openSession.mutateAsync({ sessionDate: selectedDate });
      toast.success('Đã mở buổi điểm danh.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Lỗi mở buổi điểm danh.';
      toast.error(msg);
    }
  }

  // ---------------------------------------------------------------------------
  const sessions = sessionsData?.items ?? (sessionsData as unknown as { data?: { items?: typeof sessionsData } })?.data?.items ?? [];

  return (
    <div className="space-y-4 pb-24 md:pb-4">
      {/* Date selector + session history */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <label htmlFor="session-date" className="text-sm font-medium shrink-0">
            Ngày điểm danh
          </label>
          <Input
            id="session-date"
            type="date"
            value={selectedDate}
            max={todayStr()}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="h-9 w-40"
          />
        </div>

        {/* Session status badge */}
        {hasSession && (
          <Badge
            variant={isClosed ? 'secondary' : 'success'}
            className="text-xs self-start sm:self-auto"
          >
            {isClosed ? '🔒 Đã chốt' : '🟢 Đang mở'}
          </Badge>
        )}
        {!hasSession && !isLoading && attData && (
          <Badge variant="outline" className="text-xs self-start sm:self-auto">
            Chưa mở buổi
          </Badge>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-lg" />
          ))}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>Không tải được dữ liệu điểm danh.</span>
            <Button variant="outline" size="sm" onClick={() => void refetch()}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Thử lại
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* No session yet → open button */}
      {attData && !hasSession && (
        <div className="rounded-xl border border-dashed border-border p-8 flex flex-col items-center gap-4 text-center">
          <p className="text-sm text-muted-foreground">
            Chưa có buổi điểm danh ngày{' '}
            {new Date(selectedDate).toLocaleDateString('vi-VN')}.
          </p>
          <Button
            onClick={() => void handleOpenSession()}
            disabled={openSession.isPending}
            className="min-h-[44px]"
          >
            {openSession.isPending ? 'Đang mở...' : `Mở buổi điểm danh`}
          </Button>
        </div>
      )}

      {/* Session closed banner */}
      {isClosed && (
        <Alert className="border-brand-orange-200 bg-brand-orange-50 dark:border-brand-orange-700/50 dark:bg-brand-orange-950/30">
          <Lock className="h-4 w-4 text-brand-orange-600 dark:text-brand-orange-400" />
          <AlertDescription className="text-brand-orange-700 dark:text-brand-orange-300 text-sm">
            Buổi đã chốt — cần Admin mở lại để chỉnh sửa điểm danh.
          </AlertDescription>
        </Alert>
      )}

      {/* Attendance form — shown when session exists */}
      {hasSession && records.length > 0 && (
        <>
          {/* Summary bar */}
          <div className="flex flex-wrap gap-3 text-xs font-medium rounded-lg bg-muted/50 px-4 py-2.5">
            <span className="text-brand-green-600 dark:text-brand-green-400">✓ Có mặt: {summary.present}</span>
            <span className="text-destructive">✗ Vắng: {summary.absent}</span>
            <span className="text-brand-orange-600 dark:text-brand-orange-400">⏱ Trễ: {summary.late}</span>
            <span className="text-[var(--color-info)] dark:text-[var(--color-info-border)]">📋 Có phép: {summary.excused}</span>
            <span className="text-muted-foreground">/ {records.length} HV</span>
          </div>

          {/* All-present quick action */}
          {!isClosed && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => dispatch({ type: 'set_all_present' })}
              className="min-h-[44px] w-full sm:w-auto"
            >
              <CheckSquare className="mr-2 h-4 w-4" />
              Tất cả có mặt
            </Button>
          )}

          {/* Student record cards */}
          <div className="flex flex-col gap-3">
            {records.map((r) => (
              <AttendanceRecordCard
                key={r.enrollmentId}
                record={r}
                disabled={isClosed}
                dispatch={dispatch}
              />
            ))}
          </div>
        </>
      )}

      {/* Session exists but no enrolled students */}
      {hasSession && records.length === 0 && !isLoading && (
        <p className="text-sm text-muted-foreground py-6 text-center">
          Lớp chưa có học viên ghi danh — không thể điểm danh.
        </p>
      )}

      {/* Sessions history */}
      {Array.isArray(sessions) && sessions.length > 0 && (
        <div className="space-y-2 pt-2">
          <h2 className="text-sm font-semibold text-muted-foreground">Lịch sử buổi điểm danh</h2>
          <div className="flex flex-col gap-2">
            {sessions.slice(0, 10).map((s: {
              id: string;
              sessionDate: string;
              sessionNumber?: number | null;
              status: string;
              presentCount?: number;
              totalCount?: number;
            }) => (
              <div
                key={s.id}
                className={`flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm transition-colors min-h-[44px] gap-2
                  ${selectedDate === s.sessionDate ? 'border-primary bg-primary/5' : 'border-border'}`}
              >
                <button
                  type="button"
                  onClick={() => setSelectedDate(s.sessionDate)}
                  className="flex-1 text-left hover:text-foreground"
                >
                  <span>
                    {new Date(s.sessionDate).toLocaleDateString('vi-VN')}
                    {s.sessionNumber ? ` · Buổi ${s.sessionNumber}` : ''}
                  </span>
                </button>
                <div className="flex items-center gap-2 shrink-0">
                  {s.presentCount !== undefined && s.totalCount !== undefined && (
                    <span className="text-xs text-muted-foreground">
                      {s.presentCount}/{s.totalCount}
                    </span>
                  )}
                  <Badge
                    variant={s.status === 'closed' ? 'outline' : 'success'}
                    className="text-xs"
                  >
                    {s.status === 'closed' ? '🔒 Đã chốt' : '🟢 Mở'}
                  </Badge>
                  <Link
                    to="/teacher/classes/$classId/sessions/$sessionId"
                    params={{ classId, sessionId: s.id }}
                    className="flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground transition-colors min-h-[28px]"
                    title="Vào bảng điều khiển buổi học"
                  >
                    <LayoutDashboard className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Vào lớp</span>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sticky action bar — mobile only; on md+ shown inline */}
      {hasSession && !isClosed && records.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-20 bg-background border-t border-border p-3 flex gap-2 md:static md:border-0 md:p-0 md:bg-transparent md:mt-4">
          <Button
            variant="outline"
            className="flex-1 min-h-[44px]"
            onClick={() => void handleSave(false)}
            disabled={saveSession.isPending || closeSessionMut.isPending}
          >
            <Save className="mr-2 h-4 w-4" />
            {saveSession.isPending ? 'Đang lưu...' : 'Lưu'}
          </Button>
          <Button
            className="flex-1 min-h-[44px]"
            onClick={() => void handleSave(true)}
            disabled={saveSession.isPending || closeSessionMut.isPending}
          >
            {closeSessionMut.isPending ? 'Đang chốt...' : 'Chốt buổi'}
          </Button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Individual attendance record card
// ---------------------------------------------------------------------------
const STATUS_OPTIONS: { value: AttStatus; label: string; className: string }[] = [
  { value: 'present',  label: 'Có mặt', className: 'border-brand-green-300 bg-brand-green-50 text-brand-green-700 dark:bg-brand-green-950/40 dark:text-brand-green-300 dark:border-brand-green-700 data-[active=true]:bg-brand-green-500 data-[active=true]:text-white data-[active=true]:border-brand-green-500' },
  { value: 'absent',   label: 'Vắng',   className: 'border-destructive/40 bg-destructive/5 text-destructive dark:border-destructive/60 dark:bg-destructive/10 data-[active=true]:bg-destructive data-[active=true]:text-white data-[active=true]:border-destructive' },
  { value: 'late',     label: 'Trễ',    className: 'border-brand-orange-300 bg-brand-orange-50 text-brand-orange-700 dark:bg-brand-orange-950/40 dark:text-brand-orange-300 dark:border-brand-orange-700 data-[active=true]:bg-brand-orange-500 data-[active=true]:text-white data-[active=true]:border-brand-orange-500' },
  { value: 'excused',  label: 'Phép',   className: 'border-[var(--color-info-border)]/40 bg-[var(--color-info)]/5 text-[var(--color-info)] dark:border-[var(--color-info-border)]/50 dark:bg-[var(--color-info-border)]/10 dark:text-[var(--color-info-border)] data-[active=true]:bg-[var(--color-info)] data-[active=true]:text-white data-[active=true]:border-[var(--color-info)]' },
];

function AttendanceRecordCard({
  record,
  disabled,
  dispatch,
}: {
  record: LocalRecord;
  disabled: boolean;
  dispatch: React.Dispatch<Action>;
}) {
  return (
    <div className={`rounded-lg border border-border bg-card p-3 space-y-3 ${disabled ? 'opacity-70' : ''}`}>
      {/* Name + code */}
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-foreground">{record.fullName}</p>
          <p className="text-xs font-mono text-muted-foreground">{record.studentCode}</p>
        </div>
      </div>

      {/* Status buttons — wrap on very small screens */}
      <div className="flex flex-wrap gap-2">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            data-active={record.status === opt.value}
            disabled={disabled}
            onClick={() =>
              dispatch({ type: 'set_status', enrollmentId: record.enrollmentId, status: opt.value })
            }
            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors min-h-[36px] disabled:cursor-not-allowed ${opt.className}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Check-in time — only for late */}
      {record.status === 'late' && !disabled && (
        <div className="flex items-center gap-2">
          <label className="text-xs text-muted-foreground shrink-0">Giờ vào:</label>
          <Input
            type="time"
            value={record.checkInAt}
            onChange={(e) =>
              dispatch({ type: 'set_checkin', enrollmentId: record.enrollmentId, checkInAt: e.target.value })
            }
            className="h-8 w-28 text-xs"
          />
        </div>
      )}

      {/* Note — shown for absent/late/excused */}
      {(record.status !== 'present' || record.note) && !disabled && (
        <Textarea
          value={record.note}
          onChange={(e) =>
            dispatch({ type: 'set_note', enrollmentId: record.enrollmentId, note: e.target.value })
          }
          placeholder="Ghi chú (lý do vắng/trễ...)"
          className="text-xs h-16 resize-none"
          maxLength={200}
        />
      )}

      {/* Readonly note when locked */}
      {disabled && record.note && (
        <p className="text-xs text-muted-foreground italic">{record.note}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Simple date state hook
// ---------------------------------------------------------------------------
function useDateState(initial: string): [string, (d: string) => void] {
  const [date, setDate] = useReducer((_: string, next: string) => next, initial);
  return [date, setDate];
}
